/**
 * Extracts EVERY platform campaign ID a launch produced.
 *
 * A single insertion order routinely creates several platform campaigns (three
 * Google Ads Search campaigns from one launch template, several TTD campaigns
 * from one IO). Creative assignment has to reach all of them, so this returns
 * the full list rather than collapsing to the first element the way
 * `getEffectivePlatformCampaignId()` in CampaignDetailPage does for its
 * single-ID display needs.
 *
 * Platform coverage mirrors the backend's utils/platformCampaignIdExtractor.js
 * and the extraction in UploadCreativesModal. Platforms whose assign launcher
 * takes a single campaign by contract (DV360, YouTube DV360) still return one
 * ID — the caller does not have to special-case them.
 */

const GOOGLE_ADS_DESTINATIONS = [
  'google-ads-search', 'google_search',
  'google-ads-pmax', 'google_pmax',
  'google-ads-demandgen', 'google_demandgen'
];

// Trim, drop empties, de-dupe while preserving order
const normalize = (ids) => [...new Set(
  ids.filter(Boolean).map((id) => String(id).trim()).filter(Boolean)
)];

export function getPlatformCampaignIds(campaign, orchestrationStatus) {
  if (!campaign) return [];

  // 1. Try direct campaign record. Stored comma-joined when a launch produced
  //    multiple campaigns (see the status update in routes/v2/orchestration.js).
  if (campaign.platform_campaign_id) {
    const recordIds = normalize(String(campaign.platform_campaign_id).split(','));
    if (recordIds.length > 0) return recordIds;
  }

  // 2. Try orchestration result
  const apiResult = orchestrationStatus?.apiResult;
  const details = apiResult?.details;

  if (!apiResult && !details) return [];

  const destination = campaign.destination?.toLowerCase();
  const fallbackId = details?.campaign_id || apiResult?.campaign_id;

  if (destination === 'dv360' || destination === 'youtube-dv360') {
    // DV360's assign launcher takes a single campaign by contract
    const dv360Url = details?.dv360_url || apiResult?.dv360_url;
    const dv360Match = dv360Url?.match(/\/a\/(\d+)\/c\/(\d+)/);
    if (dv360Match) return normalize([dv360Match[2]]);
    return normalize([apiResult?.campaign_id || details?.campaign_id]);
  } else if (destination === 'ttd') {
    // An insertion order can yield several TTD campaigns
    const sandboxUrls = Array.isArray(details?.sandbox_urls) && details.sandbox_urls.length > 0
      ? details.sandbox_urls
      : (details?.sandbox_url ? [details.sandbox_url] : []);
    const ttdIds = normalize(sandboxUrls.map((url) => url?.match(/\/campaign\/([^/]+)/)?.[1]));
    if (ttdIds.length > 0) return ttdIds;
    return normalize([details?.campaign_id]);
  } else if (destination === 'facebook') {
    const fbUrl = details?.fb_url || apiResult?.fb_url;
    const fbMatch = fbUrl?.match(/selected_campaign_ids=(\d+)/);
    if (fbMatch) return normalize([fbMatch[1]]);
    return normalize([fallbackId]);
  } else if (destination === 'reddit') {
    // selectedIds carries every campaign: "campaigns:id1,campaigns:id2"
    const selectedIdsMatch = details?.reddit_url?.match(/selectedIds=([^&]+)/);
    if (selectedIdsMatch) {
      const redditIds = normalize(
        (decodeURIComponent(selectedIdsMatch[1]).match(/campaigns:([^,]+)/g) || [])
          .map((entry) => entry.replace('campaigns:', ''))
      );
      if (redditIds.length > 0) return redditIds;
    }
    const redditCampaigns = details?.campaigns || apiResult?.campaigns;
    if (redditCampaigns?.length > 0) {
      const redditIds = normalize(redditCampaigns.map((c) => c.id || c.campaign_id));
      if (redditIds.length > 0) return redditIds;
    }
    return normalize([fallbackId]);
  } else if (GOOGLE_ADS_DESTINATIONS.includes(destination)) {
    const gadsCampaigns = details?.campaigns || apiResult?.campaigns;
    if (gadsCampaigns?.length > 0) {
      const gadsIds = normalize(gadsCampaigns.map((c) => c.campaign_id || c.id));
      if (gadsIds.length > 0) return gadsIds;
    }
    // Legacy shape: campaign_urls carrying ?campaignId=
    const campaignUrls = details?.campaign_urls || apiResult?.campaign_urls;
    if (campaignUrls?.length > 0) {
      const gadsIds = normalize(campaignUrls.map((url) => url?.match(/campaignId=([^&]+)/)?.[1]));
      if (gadsIds.length > 0) return gadsIds;
    }
    return normalize([fallbackId]);
  } else if (destination === 'madhive') {
    return normalize([
      details?.campaign_id ||
      details?.campaign_results?.response?.id ||
      details?.processed_config?.lineItems?.[0]?.campaignId
    ]);
  }

  return [];
}
